import React from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import {
  DogPage,
  DogFactsBar,
  DogHealthGenetics,
  RichProfileText
} from "components/Dogs/DogComponents.js";
import SEO from "components/SEO/SEO.js";
import DogGallery from "./DogGallery";

const stackImage = require("../../assets/img/dogs/squeeze/stack3.jpg");
const pedigreeImage = require("../../assets/img/dogs/litters/vr2023/pedigree.jpg");
const venusProfileImage = require("../../assets/img/dogs/profile-venus.jpg");

const galleryImages = [
  require("../../assets/img/dogs/squeeze/reg1.jpg"),
  require("../../assets/img/dogs/squeeze/reg2.jpg"),
  require("../../assets/img/dogs/squeeze/reg3.jpg"),
  require("../../assets/img/dogs/squeeze/reg4.jpg"),
  require("../../assets/img/dogs/squeeze/reg5.jpg"),
  require("../../assets/img/dogs/squeeze/reg7.jpg"),
  require("../../assets/img/dogs/squeeze/reg8.jpg")
];

const clearances = [
  {
    id: "squeeze-caer-2023",
    label: "CAER Report 2023",
    shortLabel: "CAER",
    category: "Eyes",
    detailLabel: "Eyes",
    date: "July 2023",
    image: require("../../assets/img/dogs/squeeze/clearance1.jpg"),
    alt: "Squeeze CAER report from July 2023"
  },
  {
    id: "squeeze-baer-2023",
    label: "BAER Report",
    shortLabel: "BAER",
    category: "Other",
    detailLabel: "Hearing",
    date: "July 2023",
    image: require("../../assets/img/dogs/squeeze/clearance2.jpg"),
    alt: "Squeeze "
  },
  {
    id: "squeeze-embark-2023",
    label: "Embark Report",
    shortLabel: "Embark",
    category: "Genetic",
    detailLabel: "DNA",
    date: "November 2023",
    image: require("../../assets/img/dogs/squeeze/clearance3.jpg"),
    alt: "Squeeze Embark report from March 2021"
  },
  {
    id: "squeeze-ofa-spine-2026",
    label: "OFA Spine Evaluation",
    shortLabel: "Spine",
    category: "Orthopedic",
    detailLabel: "Spine",
    date: "October 2026",
    image: require("../../assets/img/dogs/squeeze/clearance4.jpg"),
    alt: "Squeeze OFA spine evaluation showing a normal spine from October 2026"
  },
  {
    id: "squeeze-ofa-elbows-2026",
    label: "OFA Elbow Evaluation",
    shortLabel: "Elbows",
    category: "Orthopedic",
    detailLabel: "Elbows",
    date: "October 2026",
    image: require("../../assets/img/dogs/squeeze/clearance5.jpg"),
    alt: "Squeeze OFA elbow evaluation showing normal elbows from October 2026"
  },
  {
    id: "squeeze-ofa-hips-2026",
    label: "OFA Hip Evaluation",
    shortLabel: "Hips",
    category: "Orthopedic",
    detailLabel: "Hips",
    date: "October 2026",
    image: require("../../assets/img/dogs/squeeze/clearance6.jpg"),
    alt: "Squeeze OFA hip evaluation rated Good from October 2026"
  },
  {
    id: "squeeze-ofa-shoulders-2026",
    label: "OFA Shoulder Evaluation",
    shortLabel: "Shoulders",
    category: "Orthopedic",
    detailLabel: "Shoulders",
    date: "October 2026",
    image: require("../../assets/img/dogs/squeeze/clearance7.jpg"),
    alt: "Squeeze OFA shoulder evaluation showing normal shoulders from October 2026"
  },
];

const pedigreeData = {
  image: pedigreeImage,
  alt: "Venus x Rip pedigree",
  linkedDogs: [
    {
      id: "venus",
      name: "Venus",
      relationship: "Dam",
      breed: "Border Collie",
      image: venusProfileImage,
      profileHref: "/dogs/venus"
    }
  ]
};

const squeezeProfile = {
  name: "Squeeze",
  registeredName: "Endgame's Fresh Squeeze",
  about: "Squeeze is a blue merle rough coat border collie from the Venus x Rip litter. She lives and trains with Nicole Fischer and will remain part of the Endgame breeding program.",
  facts: [
    { label: "Breed", value: "Border Collie" },
    { label: "Born", value: "05/13/23" },
    { label: "Height", value: '20"' },
    { label: "Weight", value: "34lbs" }
  ],
  temperament: "Squeeze is a joy. She blends a gentle, cuddly nature with badass, playful energy. She possesses a great off-switch and remarkable patience. She always tries to be a good girl and sits perfectly still for nail trims, bathing, and vet exams, and has a super solid recall. When meeting new people or dogs, she sometimes is friendly and approaches and sometimes decides against it, but her natural curiosity often has her warm up quickly. Overall, Squeeze is highly balanced in both body and mind.",
  athleticAbility: "Squeeze embodies pure athleticism and drive on the agility course. She lives to work and is highly resilient and ready to do whatever you ask on repeat. She tends toward obstacle focus but remains thoughtful when in drive (despite the occasional screaming!). While toys are her highest value reward, she works just as happily for food and praise.",
  video: {
    src: "https://www.youtube.com/embed/videoseries?si=BvZVdePFGhZJZkbt&amp;list=PLPQveJv1WtGs"
  }
};

const healthGroups = [
  {
    title: "Orthopedic & Screening",
    items: [
      { label: "Hips", value: "Good" },
      { label: "Elbows", value: "Normal" },
      { label: "Shoulders", value: "Normal" },
      { label: "Spine", value: "Normal" },
      { label: "Eyes", value: "Normal (2023)" },
      { label: "Hearing", value: "BAER Normal" }
    ]
  },
  {
    title: "DNA Panel",
    items: [
      { label: "CEA", value: "Clear" },
      { label: "DM", value: "Clear" },
      { label: "SN", value: "Clear" },
      { label: "TNS", value: "Clear" },
      { label: "IGS", value: "Clear" },
      { label: "NCL5", value: "Clear" },
      { label: "G/G", value: "Clear" },
      { label: "DH", value: "Clear" },
      { label: "EOD", value: "Clear" }
    ]
  }
];

function DogSectionHeading({ eyebrow, title, children }) {
  return (
    <div className="dog-detail-section-heading">
      {eyebrow && <p>{eyebrow}</p>}
      <h2>{title}</h2>
      <span aria-hidden="true"></span>
      {children}
    </div>
  );
}

function SqueezePage() {
  const hasVideo = Boolean(squeezeProfile.video && squeezeProfile.video.src);
  return (
    <>
      <SEO
        title="Squeeze | Border Collie Sport Dog"
        description="Meet Squeeze, a blue merle rough coat border collie from the Venus x Rip 2023 litter and part of the Endgame Sport Dogs breeding program for future sport dog puppies."
        path="/dogs/squeeze"
      />
      <DogPage
        headerBg="squeeze"
        pageClassName="dog-detail-page dog-squeeze-page"
      >
        <section className="dog-facts-wrap" aria-label="Squeeze at a glance">
          <Container>
            <DogFactsBar facts={squeezeProfile.facts} />
          </Container>
        </section>

        <section className="dog-detail-section dog-about-section">
          <Container>
            <div className="dog-about-layout">
              <div className="dog-about-copy">
                <div className="dog-back-link">
                  <Link to="/dogs">
                    <ArrowLeft aria-hidden="true" size={14} strokeWidth={2.2} /> Back to Dogs
                  </Link>
                </div>
                <DogSectionHeading eyebrow={squeezeProfile.registeredName} title={`About ${squeezeProfile.name}`} />
                <p><RichProfileText>{squeezeProfile.about}</RichProfileText></p>
              </div>
              <figure className="dog-stack-figure">
                <img alt="Squeeze stacked in profile" src={stackImage} />
                <figcaption>{squeezeProfile.stats}</figcaption>
              </figure>
            </div>

            <DogHealthGenetics
              dogName={squeezeProfile.name}
              breed={squeezeProfile.facts.find(fact => fact.label === "Breed")?.value}
              healthGroups={healthGroups}
              pedigreeData={pedigreeData}
              clearances={clearances}
            />

            <div className="dog-traits-layout">
              <article>
                <h2>Temperament</h2>
                <span aria-hidden="true"></span>
                <p><RichProfileText>{squeezeProfile.temperament}</RichProfileText></p>
              </article>
              <article>
                <h2>Athletic Ability</h2>
                <span aria-hidden="true"></span>
                <p><RichProfileText>{squeezeProfile.athleticAbility}</RichProfileText></p>
              </article>
            </div>
          </Container>
        </section>

        <div className="dog-performance-wrap">
          {hasVideo && (
            <section className="dog-detail-section dog-video-section">
              <Container>
                <DogSectionHeading eyebrow={`Squeeze in Action`} title="Sport highlights" />
                <div className="dog-video-feature">
                  <div className="dog-video-embed">
                    <div className="video-frame">
                      <iframe
                        src={squeezeProfile.video.src}
                        title={squeezeProfile.video.title}
                        loading="lazy"
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                  <div className="dog-video-caption">
                    <p>Squeeze is starting to compete with her owner in agility. They've done multiple venues including UKI and AKC (both regular and ISC events). She has a nice jumping form in both extension and collection and will work hard to figure out the correct obstacle when she it isn't clear.</p>
                  </div>
                </div>
              </Container>
            </section>
          )}
        </div>

        <section className="dog-detail-section dog-gallery-section site-bg-dark-navy">
          <Container>
            <DogSectionHeading title="Photo Gallery" />
          </Container>
          <DogGallery images={galleryImages} />
        </section>
      </DogPage>
    </>
  );
}

export default SqueezePage;
